'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/**
 * DmtOnboardingWidget — registers this merchant as a DMT Business
 * Correspondent agent with NSDL: Start (creates the ServiceKyc record via
 * initialize) -> Agent Registration (name/lastname/PAN) -> Complete. Mirrors
 * webpayment's components/Vendor/Onboarding/DMT/Onboard.vue and its
 * useDmtBcApi calls, ported onto client.dmt.initializeAgent/registerAgent
 * (dmt.bc.route.js's /onboarding/dmt/bc/agent/* sub-routes — distinct from
 * DmtWidget's customer/remitter onboarding, which is the merchant's own
 * customers, not the merchant itself).
 *
 * initializeAgent()'s success response carries no top-level statusCode at
 * all (dmt.bc.controller.js#initialize returns bare {merchantId, service,
 * interface} on success) — this widget checks `res.merchantId` as a
 * fallback for exactly that reason, same as Onboard.vue#handleStep1's
 * `res?.statusCode === '00' || res?.merchantId`.
 *
 * const widget = new DmtOnboardingWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-dmt-onboarding').start();
 */
class DmtOnboardingWidget extends BaseWidget {
  start() {
    this._p = {};
    this.screenStart();
    return this;
  }

  stepSubtitle(n, text) {
    return `Step ${n} of 3 · ${text}`;
  }

  screenStart(errorMsg) {
    this.setHeader({ title: 'DMT Onboarding', subtitle: this.stepSubtitle(1, 'Get Started'), showBack: false });
    this.showScreen(
      h('div', {}, [
        errorMsg ? this.banner('error', errorMsg) : null,
        h('div', { class: 'bbx-card' }, [
          h('p', {
            class: 'bbx-status-sub',
            style: { margin: 0, textAlign: 'left' },
            text: 'This registers you for Domestic Money Transfer (DMT) services as an NSDL Business Correspondent agent. You will need a PAN card to complete registration.',
          }),
        ]),
        h('button', {
          class: 'bbx-btn',
          text: 'Initialize & Continue',
          onClick: async (e) => {
            e.target.disabled = true;
            this.renderLoading('Initializing DMT registration…');
            try {
              const res = await this.client.dmt.initializeAgent();
              const ok = res?.statusCode === '00' || !!res?.merchantId;
              if (!ok) {
                this.fail(res, res?.message);
                this.screenStart(res?.message || 'Could not initialize DMT registration');
                return;
              }
              this.screenAgent();
            } catch (err) {
              this.fail(err);
              this.screenStart(this.errorMessage(err, 'Could not initialize DMT registration'));
            }
          },
        }),
      ]),
    );
  }

  screenAgent(errorMsg) {
    this.setHeader({ title: 'Agent Registration', subtitle: this.stepSubtitle(2, 'Agent Registration'), showBack: false });

    const nameField = this.field({ label: 'First Name', placeholder: 'e.g. John', value: this._p.name || '', onInput: (v) => { this._p.name = v; } });
    const lastNameField = this.field({ label: 'Last Name', placeholder: 'e.g. Snow', value: this._p.lastname || '', onInput: (v) => { this._p.lastname = v; } });
    const panField = this.field({ label: 'PAN Card', placeholder: 'e.g. ABCDE1234F', maxlength: '10', value: this._p.pancard || '', onInput: (v) => { this._p.pancard = (v || '').toUpperCase(); } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        nameField.node, lastNameField.node, panField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Register Agent',
          onClick: async (e) => {
            if (!this._p.name || !this._p.name.trim()) {
              errorSlot.replaceChildren(this.banner('error', 'First name is required'));
              return;
            }
            if (!this._p.lastname || !this._p.lastname.trim()) {
              errorSlot.replaceChildren(this.banner('error', 'Last name is required'));
              return;
            }
            const pancard = (this._p.pancard || '').toUpperCase();
            if (!PAN_REGEX.test(pancard)) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid PAN (e.g. ABCDE1234F)'));
              return;
            }
            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Registering agent with NSDL…');
            try {
              const res = await this.client.dmt.registerAgent({
                name: this._p.name.trim(),
                lastname: this._p.lastname.trim(),
                pancard,
                dmt: 1,
                aeps: 0,
                cardpin: 0,
                accountopen: 0,
              });
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                e.target.disabled = false;
                this.screenAgent(res?.data?.message || res.message || 'Agent registration failed');
                return;
              }
              this._p.pancard = pancard;
              this._p.result = res.data || res;
              this.screenResult();
            } catch (err) {
              this.fail(err);
              e.target.disabled = false;
              this.screenAgent(this.errorMessage(err, 'Agent registration failed'));
            }
          },
        }),
      ]),
    );
  }

  screenResult() {
    this.setHeader({ title: 'DMT Onboarding', showBack: false });
    const payload = { ...this._p.result, name: this._p.name, lastname: this._p.lastname, pancard: this._p.pancard };

    const resultRows = Object.entries(this._p.result || {})
      .filter(([, v]) => v != null && typeof v !== 'object')
      .map(([k, v]) => h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: k }), h('span', { class: 'bbx-row-v bbx-mono', text: String(v) })]));

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: BX.success2 }, text: '✓' }),
        h('p', { class: 'bbx-status-title', text: 'DMT Onboarding Complete' }),
        h('p', { class: 'bbx-status-sub', text: 'You are now registered as an NSDL DMT agent.' }),
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Agent Name' }), h('span', { class: 'bbx-row-v', text: `${this._p.name || ''} ${this._p.lastname || ''}`.trim() || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'PAN' }), h('span', { class: 'bbx-row-v bbx-mono', text: this._p.pancard || '—' })]),
          ...resultRows,
        ]),
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { this.succeed(payload); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { DmtOnboardingWidget };
